/**
 * The app session store (S097; M12). The single source of truth for "who am I and what is my token" across a
 * Vello real client. It:
 *  - restores a persisted session from the `SecureStore` at boot (survives an app relaunch);
 *  - records a fresh session after an OTP verify, rotates it after a refresh, and clears it on sign-out;
 *  - exposes `accessToken()` so the `ApiClient` attaches the *current* bearer on every call (a rotation is
 *    visible on the very next request);
 *  - decides when the access token is stale (`needsRefresh`) so the app refreshes proactively.
 *
 * The persisted payload is exactly the token triple + principal — never a full profile, never a code. The
 * serialize/deserialize seam is pure and independently tested, and a corrupt stored value fails closed to
 * anonymous rather than throwing at boot.
 */
import { CONSUMER_REALMS, OPERATOR_REALMS } from '@vello/api-client';
import { isAcknowledgedStore } from './secure-store.js';
export const ANONYMOUS = { status: 'anonymous' };
// The full realm set a persisted session may carry, derived from the api-client's canonical classes (there is
// no ADMIN realm — the Admin console rides an operator realm; see `@vello/api-client` auth.ts).
const REALMS = new Set([...CONSUMER_REALMS, ...OPERATOR_REALMS]);
/** Serialize the durable part of a session for the secure store. Pure. */
export function serializeSession(state) {
    return JSON.stringify(state);
}
/** Parse a stored session; returns undefined for any malformed/incomplete payload (fail closed to anonymous). */
export function deserializeSession(raw) {
    let v;
    try {
        v = JSON.parse(raw);
    }
    catch {
        return undefined;
    }
    if (typeof v !== 'object' || v === null)
        return undefined;
    const o = v;
    const t = o.tokens;
    const p = o.principal;
    if (t === undefined || p === undefined)
        return undefined;
    const { accessToken, refreshToken, accessTokenExpiresAt } = t;
    const { userId, sessionId, realm } = p;
    if (typeof accessToken !== 'string' ||
        typeof refreshToken !== 'string' ||
        typeof accessTokenExpiresAt !== 'string' ||
        typeof userId !== 'string' ||
        typeof sessionId !== 'string' ||
        typeof realm !== 'string' ||
        !REALMS.has(realm)) {
        return undefined;
    }
    return {
        tokens: { accessToken, refreshToken, accessTokenExpiresAt },
        principal: { userId, sessionId, realm: realm },
    };
}
const STORAGE_KEY = 'vello.session.v1';
function toStored(session, realm) {
    return {
        tokens: {
            accessToken: session.accessToken,
            refreshToken: session.refreshToken,
            accessTokenExpiresAt: session.accessTokenExpiresAt,
        },
        principal: { userId: session.userId, sessionId: session.sessionId, realm },
    };
}
/** A stored logout epoch: a positive safe integer; anything else is corrupt (unverifiable — never silently 0). */
function parseLogoutEpoch(raw) {
    if (!/^[1-9]\d{0,15}$/.test(raw))
        return undefined;
    const n = Number(raw);
    return Number.isSafeInteger(n) ? n : undefined;
}
/**
 * A fresh logout epoch (ADR-0039 amendment 5): a uniformly random integer in [1, 2^53 − 1]. Epochs are compared by
 * equality only, so all that matters is that a new epoch never equals one another context captured (≈ 2^-53 per pair).
 */
function freshLogoutEpoch() {
    const words = new Uint32Array(2);
    const cryptoApi = globalThis.crypto;
    if (typeof cryptoApi?.getRandomValues === 'function')
        cryptoApi.getRandomValues(words);
    else
        words.set([Math.floor(Math.random() * 0x1_0000_0000), Math.floor(Math.random() * 0x1_0000_0000)]);
    const [high = 0, low = 0] = words;
    const epoch = (high & 0x1f_ffff) * 0x1_0000_0000 + low;
    return epoch === 0 ? 1 : epoch;
}
export class SessionStore {
    secureStore;
    key;
    state = ANONYMOUS;
    constructor(secureStore, key = STORAGE_KEY) {
        this.secureStore = secureStore;
        this.key = key;
    }
    /** Restore a persisted session at boot. A corrupt payload is discarded and the store stays anonymous. */
    async load() {
        const raw = await this.secureStore.get(this.key);
        if (raw === undefined) {
            this.state = ANONYMOUS;
            return this.state;
        }
        const stored = deserializeSession(raw);
        if (stored === undefined) {
            await this.secureStore.remove(this.key);
            this.state = ANONYMOUS;
            return this.state;
        }
        this.state = { status: 'authenticated', tokens: stored.tokens, principal: stored.principal };
        return this.state;
    }
    /** Persist the session produced by a successful OTP verify under the realm it authenticated. */
    async signIn(session, realm) {
        const stored = toStored(session, realm);
        await this.secureStore.set(this.key, serializeSession(stored));
        this.state = { status: 'authenticated', tokens: stored.tokens, principal: stored.principal };
        return this.state;
    }
    /** Apply a rotation. Keeps the principal; swaps the token triple. No-op when anonymous. */
    async rotate(refreshed) {
        if (this.state.status !== 'authenticated' || this.state.principal === undefined)
            return this.state;
        const stored = {
            tokens: {
                accessToken: refreshed.accessToken,
                refreshToken: refreshed.refreshToken,
                accessTokenExpiresAt: refreshed.accessTokenExpiresAt,
            },
            principal: this.state.principal,
        };
        await this.secureStore.set(this.key, serializeSession(stored));
        this.state = { status: 'authenticated', tokens: stored.tokens, principal: stored.principal };
        return this.state;
    }
    /** Clear the session locally (a server logout is a separate `AuthApi.logout` call). */
    async signOut() {
        await this.secureStore.remove(this.key);
        this.state = ANONYMOUS;
        return this.state;
    }
    /**
     * The shared logout generation (ADR-0039 amendment 2) — the "epoch". Ending a session publishes a fresh one in the shared
     * store; a refresh that captured an older epoch must discard its successor instead of persisting it (no resurrection
     * after logout). Epochs are compared by EQUALITY only (amendment 5).
     */
    async logoutGeneration() {
        // Amendment 4 (Codex r4 P1-1): an ACKNOWLEDGED read of the shared store. `undefined` means the shared epoch could not be
        // verified (unreadable or corrupt) — never silently 0; only a genuinely absent key (never logged out, or retired) is 0.
        const read = await this.readShared(this.epochKey);
        if (!read.ok)
            return undefined;
        return read.value === undefined ? 0 : parseLogoutEpoch(read.value);
    }
    /**
     * Capture the epoch a sign-in will commit under (amendment 5; Codex r5 P1). Unlike {@link logoutGeneration}, the captured
     * epoch is always PRESENT in the shared store: an absent key is first initialized with a fresh epoch through an
     * acknowledged write. That is what lets a logout whose epoch write is refused still retire the epoch by removal — an
     * absent epoch (0) never equals a captured one. `undefined` = unverifiable or not initializable: refuse before minting.
     */
    async captureLogoutGeneration() {
        const read = await this.readShared(this.epochKey);
        if (!read.ok)
            return undefined;
        if (read.value !== undefined)
            return parseLogoutEpoch(read.value);
        const initial = freshLogoutEpoch();
        return (await this.writeShared(this.epochKey, String(initial))) ? initial : undefined;
    }
    /**
     * Publish a fresh epoch (the tombstone). It is the FIRST step of ending a session (ADR-0039 amendment 3): a login or
     * refresh that captured the previous epoch can then no longer commit. Returns the new epoch, or `undefined` when the
     * shared store did not acknowledge the write (a tab's private memory never counts as a bump). Amendment 5: a fresh
     * random value, never `current + 1`, so an epoch that was retired and restarted can never re-issue a value a pending
     * sign-in captured (no ABA).
     */
    async bumpLogoutGeneration() {
        const next = freshLogoutEpoch();
        return (await this.writeShared(this.epochKey, String(next))) ? next : undefined;
    }
    /**
     * Retire the epoch by REMOVAL, confirmed through an acknowledged read (amendment 5; Codex r5 P1) — the fail-closed
     * tombstone of a logout whose epoch write the shared store refused even after the session removal freed space. Removal
     * is the one mutation an exhausted quota cannot refuse, and every sign-in captured a PRESENT epoch, so a pending sign-in
     * can no longer commit. Returns whether the shared store confirmed the epoch is gone.
     */
    async retireLogoutGeneration() {
        try {
            await this.secureStore.remove(this.epochKey);
        }
        catch {
            /* the acknowledged read below decides */
        }
        const read = await this.readShared(this.epochKey);
        return read.ok && read.value === undefined;
    }
    /**
     * Commit a session through an acknowledged shared write (the establishment step of the logout-generation protocol).
     * Returns `undefined` — and adopts nothing — when the shared store refused it.
     */
    async commitSignIn(session, realm) {
        const stored = toStored(session, realm);
        if (!(await this.writeShared(this.key, serializeSession(stored))))
            return undefined;
        this.state = { status: 'authenticated', tokens: stored.tokens, principal: stored.principal };
        return this.state;
    }
    get epochKey() {
        return `${this.key}.logout-generation`;
    }
    async readShared(key) {
        try {
            if (isAcknowledgedStore(this.secureStore))
                return await this.secureStore.getShared(key);
            return { ok: true, value: await this.secureStore.get(key) }; // a store that reports its failures by throwing
        }
        catch {
            return { ok: false };
        }
    }
    async writeShared(key, value) {
        try {
            if (isAcknowledgedStore(this.secureStore))
                return await this.secureStore.setShared(key, value);
            await this.secureStore.set(key, value);
            return true;
        }
        catch {
            return false;
        }
    }
    /**
     * Stage a session in THIS context's memory only — not persisted, invisible to other tabs — e.g. to call `/v1/me` with a
     * just-minted bearer before the session is committed under the lock. `load()` discards a staged session.
     */
    stage(session, realm) {
        const stored = toStored(session, realm);
        this.state = { status: 'authenticated', tokens: stored.tokens, principal: stored.principal };
        return this.state;
    }
    /**
     * Clear the shared session only if it is still `expectedSessionId` — so an older logout never erases a newer login that
     * committed meanwhile; otherwise adopt whatever session is stored now.
     */
    async clearIfCurrent(expectedSessionId) {
        const raw = await this.secureStore.get(this.key);
        const stored = raw === undefined ? undefined : deserializeSession(raw);
        if (stored === undefined || stored.principal.sessionId === expectedSessionId)
            return this.signOut();
        this.state = { status: 'authenticated', tokens: stored.tokens, principal: stored.principal };
        return this.state;
    }
    snapshot() {
        return this.state;
    }
    accessToken() {
        return this.state.tokens?.accessToken;
    }
    refreshToken() {
        return this.state.tokens?.refreshToken;
    }
    /**
     * Whether the access token is expired (or within `skewSeconds` of it) at `now`. An unparseable expiry is
     * treated as expired so the app refreshes rather than sending a token it cannot reason about.
     */
    needsRefresh(now, skewSeconds = 60) {
        const tokens = this.state.tokens;
        if (tokens === undefined)
            return false;
        const exp = Date.parse(tokens.accessTokenExpiresAt);
        const nowMs = Date.parse(now);
        if (Number.isNaN(exp) || Number.isNaN(nowMs))
            return true;
        return nowMs >= exp - skewSeconds * 1000;
    }
}
//# sourceMappingURL=session.js.map